/**
 * Список кривых и добавление новой.
 *
 * Легенда обязательна, как только кривых больше одной: без неё формула
 * относится к активной, а на плоскости их четыре, и какая из них чья —
 * приходится угадывать по цвету.
 */

import { CurveCard } from "./CurveCard";
import {
  FAMILY_NAMES, MAX_CURVES,
  type CurveFamily, type FunctionCurve, type LabScene, type ParameterKey,
} from "../../lib/function-lab/model";
import type { ExactNumber } from "../../lib/function-lab/numbers";

/**
 * Что можно добавить. Вида с вершиной здесь нет: он не заводится с нуля, в
 * него приходят преобразованием общего вида — тем единственным действием,
 * ради которого он и существует.
 *
 * Окружность и эллипс стоят отдельной группой с подписью: это не функции, и
 * ученик должен видеть границу ещё до того, как нажал кнопку.
 */
const FUNCTIONS: CurveFamily[] = ["line", "parabola", "reciprocal", "abs", "sqrt", "cubic"];
const RELATIONS: CurveFamily[] = ["circle", "ellipse"];

/** На кнопке в треть панели полное имя не помещается. */
const SHORT_NAMES: Partial<Record<CurveFamily, string>> = { cubic: "Кубическая" };

export function CurveList({
  scene, onSelect, onToggle, onRemove, onReset, onAdd, onParameter, onConvert,
}: {
  scene: LabScene;
  onSelect: (id: string) => void;
  onToggle: (id: string) => void;
  onRemove: (id: string) => void;
  onReset: (id: string) => void;
  onAdd: (family: CurveFamily) => void;
  onParameter: (id: string, key: ParameterKey, value: ExactNumber, final: boolean) => void;
  onConvert: (id: string, curve: FunctionCurve) => void;
}) {
  const full = scene.curves.length >= MAX_CURVES;
  return <div className="fn-lab__list">
    <ul className="fn-lab__cards" aria-label="Кривые на плоскости">
      {scene.curves.map((entry) => (
        <CurveCard key={entry.id} entry={entry} active={entry.id === scene.activeId}
          removable={scene.curves.length > 1}
          onSelect={() => onSelect(entry.id)} onToggle={() => onToggle(entry.id)}
          onRemove={() => onRemove(entry.id)} onReset={() => onReset(entry.id)}
          onParameter={(key, value, final) => onParameter(entry.id, key, value, final)}
          onConvert={(curve) => onConvert(entry.id, curve)} />
      ))}
    </ul>
    <div className="fn-lab__add">
      <div className="fn-lab__add-row" role="group" aria-label="Добавить функцию">
        {FUNCTIONS.map((family) => (
          <button key={family} className="btn btn--quiet" disabled={full}
            aria-label={`Добавить: ${FAMILY_NAMES[family]}`}
            onClick={() => onAdd(family)}>+ {SHORT_NAMES[family] ?? FAMILY_NAMES[family]}</button>
        ))}
      </div>
      <p className="fn-lab__add-caption" id="fn-lab-relations">Не функции — уравнения с x и y</p>
      <div className="fn-lab__add-row" role="group" aria-labelledby="fn-lab-relations">
        {RELATIONS.map((family) => (
          <button key={family} className="btn btn--quiet" disabled={full}
            aria-label={`Добавить: ${FAMILY_NAMES[family]}`}
            onClick={() => onAdd(family)}>+ {FAMILY_NAMES[family]}</button>
        ))}
      </div>
    </div>
    {full ? (
      <p className="fn-lab__note" role="status">
        На одной плоскости можно сравнивать до четырёх графиков.
      </p>
    ) : null}
  </div>;
}
